import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Award, Clock, Download, FileSpreadsheet, Target, Info, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { normalizeNote } from '../utils/normalizeNote';
import { exportDashboardStatsToExcel, exportStagiairesToExcel, exportNotesToExcel } from '../utils/excelExport';
import { toast } from 'sonner';
import IAAnalyticsCentre from '../components/IAAnalyticsCentre';

const COLORS_PIE = ['#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];
const COLORS_PRESENCE = { present: '#22c55e', absent: '#ef4444', retard: '#f59e0b', absent_justifie: '#3b82f6' };
const LABELS_PRESENCE = { present: 'Présent', absent: 'Absent', retard: 'Retard', absent_justifie: 'Absence justifiée' };

const SectionInfo = ({ text }) => (
  <p className="text-xs text-slate-500 mt-1 flex items-start gap-1">
    <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
    <span>{text}</span>
  </p>
);

const Analytics = ({ user }) => {
  const [stats, setStats] = useState({
    stagiaires: 0, formateurs: 0, tuteurs: 0,
    moyenne_generale: 0, total_evaluations: 0,
    par_centre: {}, moyennes_categories: {},
    evolution_notes: []
  });
  const [stagiaires, setStagiaires] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCentre, setSelectedCentre] = useState('all');
  const [competencesStats, setCompetencesStats] = useState(null);
  const [presencesStats, setPresencesStats] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [selectedCentre]);

  const loadAnalytics = async () => {
    try {
      const [stagRes, formRes, tutRes, compStatsRes, presStatsRes] = await Promise.all([
        axios.get('/stagiaires'),
        axios.get('/formateurs'),
        axios.get('/tuteurs'),
        axios.get('/competences/stats', { params: { centre: selectedCentre } }).catch(() => ({ data: null })),
        axios.get('/presences/stats-analytics', { params: { centre: selectedCentre } }).catch(() => ({ data: null }))
      ]);

      let stagiairesData = stagRes.data;
      if (selectedCentre && selectedCentre !== 'all') {
        stagiairesData = stagiairesData.filter(s => s.centre_formation === selectedCentre);
      }
      setStagiaires(stagiairesData);
      setCompetencesStats(compStatsRes.data);
      setPresencesStats(presStatsRes.data);

      // Répartition par centre
      const parCentre = stagiairesData.reduce((acc, s) => {
        const centre = s.centre_formation || 'Non défini';
        acc[centre] = (acc[centre] || 0) + 1;
        return acc;
      }, {});

      // Charger toutes les notes des stagiaires
      const allNotes = [];
      for (const stag of stagiairesData) {
        try {
          const notesRes = await axios.get(`/notes/stagiaire/${stag.user_id}`);
          allNotes.push(...notesRes.data.map(n => ({
            ...n,
            stagiaire_id: stag.user_id,
            stagiaire_nom: `${stag.user_info?.prenom || ''} ${stag.user_info?.nom || ''}`
          })));
        } catch (e) { /* skip */ }
      }
      setNotes(allNotes);

      // Normaliser toutes les notes sur /10
      const moyenne = allNotes.length > 0
        ? allNotes.reduce((acc, n) => acc + normalizeNote(n), 0) / allNotes.length
        : 0;

      // Moyennes par catégorie/matière (normalisées /10)
      const moyennesCategories = allNotes.reduce((acc, n) => {
        const cat = n.categorie || n.matiere || 'Autre';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(normalizeNote(n));
        return acc;
      }, {});

      const moyennesCat = Object.entries(moyennesCategories).reduce((acc, [cat, vals]) => {
        acc[cat] = parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2));
        return acc;
      }, {});

      // Évolution par mois (normalisée /10)
      const notesByMonth = allNotes.reduce((acc, n) => {
        const d = n.date || n.created_at;
        if (!d) return acc;
        const month = new Date(d).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
        if (!acc[month]) acc[month] = [];
        acc[month].push(normalizeNote(n));
        return acc;
      }, {});

      const evolution = Object.entries(notesByMonth)
        .map(([month, vals]) => ({
          mois: month,
          moyenne: parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1))
        }))
        .slice(-6);

      setStats({
        stagiaires: stagiairesData.length,
        formateurs: formRes.data.length,
        tuteurs: tutRes.data.length,
        moyenne_generale: moyenne.toFixed(2),
        total_evaluations: allNotes.length,
        par_centre: parCentre,
        moyennes_categories: moyennesCat,
        evolution_notes: evolution
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleExportDashboard = () => {
    exportDashboardStatsToExcel(stats);
    toast.success('Rapport Excel téléchargé');
  };
  const handleExportStagiaires = () => {
    exportStagiairesToExcel(stagiaires);
    toast.success('Liste stagiaires exportée');
  };
  const handleExportNotes = () => {
    exportNotesToExcel(notes);
    toast.success('Notes exportées');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Chargement des analytics...</div>;
  }

  const parCentreData = Object.entries(stats.par_centre).map(([name, value]) => ({ name, value }));
  const moyennesData = Object.entries(stats.moyennes_categories).map(([name, value]) => ({ name, moyenne: parseFloat(value) }));

  // Présences formatées
  const presencesChartData = presencesStats?.stats_par_statut
    ? Object.entries(presencesStats.stats_par_statut)
        .filter(([, v]) => v > 0)
        .map(([key, value]) => ({
          name: LABELS_PRESENCE[key] || key,
          value,
          fill: COLORS_PRESENCE[key] || '#94a3b8'
        }))
    : [];

  // Top 10 par moyenne (normalisée /10)
  const classement = stagiaires
    .map(stag => {
      const stagNotes = notes.filter(n => n.stagiaire_id === stag.user_id);
      const moy = stagNotes.length > 0
        ? stagNotes.reduce((acc, n) => acc + normalizeNote(n), 0) / stagNotes.length
        : 0;
      return { ...stag, moyenne: moy, nbEval: stagNotes.length };
    })
    .filter(s => s.nbEval > 0)
    .sort((a, b) => b.moyenne - a.moyenne)
    .slice(0, 10);

  return (
    <div className="space-y-8" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Analytics & Rapports</h1>
          <p className="text-slate-600">Tableau de bord analytique du centre de formation</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <select
            value={selectedCentre}
            onChange={(e) => setSelectedCentre(e.target.value)}
            className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="filter-centre"
          >
            <option value="all">Tous les centres</option>
            <option value="Atlantique">Atlantique</option>
            <option value="Occitanie">Occitanie</option>
          </select>
          <div className="flex gap-2">
            <Button onClick={handleExportDashboard} variant="outline" size="sm" data-testid="export-dashboard">
              <Download className="h-4 w-4 mr-2" />Rapport Complet
            </Button>
            <Button onClick={handleExportStagiaires} variant="outline" size="sm" data-testid="export-stagiaires">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Stagiaires
            </Button>
            <Button onClick={handleExportNotes} variant="outline" size="sm" data-testid="export-notes">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Notes
            </Button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-[#8DC63F]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Stagiaires</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.stagiaires}</p>
            </div>
            <Users className="h-7 w-7 text-[#8DC63F]" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Moyenne Générale</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.moyenne_generale}<span className="text-lg text-slate-400">/10</span></p>
            </div>
            <TrendingUp className="h-7 w-7 text-blue-500" />
          </div>
          <SectionInfo text="Moyenne normalisée sur /10 (notes d'examens /20 converties)" />
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Notes saisies</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.total_evaluations}</p>
            </div>
            <Award className="h-7 w-7 text-purple-500" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Formateurs + Tuteurs</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.formateurs + stats.tuteurs}</p>
            </div>
            <Users className="h-7 w-7 text-orange-500" />
          </div>
        </div>
        {presencesStats && (
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Taux de présence</p>
                <p className="text-3xl font-display font-bold text-[#0F172A]">{presencesStats.taux_presence}<span className="text-lg text-slate-400">%</span></p>
              </div>
              <CheckCircle className="h-7 w-7 text-emerald-500" />
            </div>
            <SectionInfo text={`${presencesStats.total_pointages} pointages enregistrés`} />
          </div>
        )}
      </div>

      {/* Assistant IA Centre */}
      <IAAnalyticsCentre selectedCentre={selectedCentre} stagiaires={stagiaires} />

      {/* Section Compétences Entreprise (évaluations tuteur/entreprise) */}
      {competencesStats && competencesStats.total_evaluations > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-display font-semibold text-[#0F172A]">Compétences en entreprise</h2>
            <SectionInfo text="Basé sur les évaluations faites par le tuteur et l'entreprise (compétent / en cours / non compétent)" />
          </div>

          {/* Mini KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">{competencesStats.stats_by_statut?.competent || 0}</p>
              <p className="text-xs text-green-700 font-medium">Compétent</p>
              <p className="text-xs text-green-500">{competencesStats.pourcentages?.competent || 0}%</p>
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
              <p className="text-2xl font-bold text-orange-600">{competencesStats.stats_by_statut?.en_cours || 0}</p>
              <p className="text-xs text-orange-700 font-medium">En cours</p>
              <p className="text-xs text-orange-500">{competencesStats.pourcentages?.en_cours || 0}%</p>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-600">{competencesStats.stats_by_statut?.non_competent || 0}</p>
              <p className="text-xs text-red-700 font-medium">Non compétent</p>
              <p className="text-xs text-red-500">{competencesStats.pourcentages?.non_competent || 0}%</p>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-700">{competencesStats.total_evaluations || 0}</p>
              <p className="text-xs text-slate-600 font-medium">Total évaluations</p>
              <p className="text-xs text-slate-500">{competencesStats.total_stagiaires || 0} stagiaires</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Heures d'encadrement */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" />
                Heures d'encadrement en entreprise
              </h3>
              <SectionInfo text="Heures d'encadrement saisies par les tuteurs pour chaque stagiaire" />
              
              {competencesStats.heures_encadrement?.total_heures > 0 ? (
                <>
                  <div className="grid grid-cols-3 gap-2 mt-3 mb-3">
                    <div className="text-center p-2 bg-white rounded border border-blue-100">
                      <p className="text-xl font-bold text-blue-600">{competencesStats.heures_encadrement.total_heures}h</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-blue-100">
                      <p className="text-xl font-bold text-blue-600">{competencesStats.heures_encadrement.moyenne}h</p>
                      <p className="text-xs text-slate-500">Moy/stagiaire</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-blue-100">
                      <p className="text-xl font-bold text-blue-600">{competencesStats.heures_encadrement.nb_stagiaires_avec_heures}</p>
                      <p className="text-xs text-slate-500">Stagiaires</p>
                    </div>
                  </div>
                  
                  {/* Détail par stagiaire */}
                  {competencesStats.heures_encadrement.detail?.map((s, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100 mb-1">
                      <div>
                        <p className="text-sm font-medium text-slate-700">{s.nom}</p>
                        <p className="text-xs text-slate-500">{s.nb_sessions} sessions - {s.competences_validees}/{s.competences_total} compétences ({s.pct_validation}%)</p>
                      </div>
                      <span className="text-sm font-bold text-blue-600">{s.heures}h</span>
                    </div>
                  ))}
                  
                  <p className="text-xs text-slate-500 mt-2">
                    {competencesStats.stagiaires_autonomes} stagiaire(s) autonome(s) (>= 80% compétences validées)
                  </p>
                </>
              ) : (
                <div className="text-center py-6 text-slate-400">
                  <Clock className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-sm">Aucune heure d'encadrement saisie</p>
                  <p className="text-xs">Les tuteurs peuvent saisir les heures depuis leur espace</p>
                </div>
              )}
            </div>
          </div>

          {/* Top catégories */}
          {competencesStats.top_categories && competencesStats.top_categories.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Top catégories validées</h3>
              <div className="space-y-2">
                {competencesStats.top_categories.map((cat, idx) => (
                  <div key={cat.categorie} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium text-slate-700">{idx + 1}. {cat.categorie}</span>
                    <div className="flex gap-3 text-xs">
                      <span className="text-green-600">{cat.competent} validés</span>
                      <span className="text-orange-500">{cat.en_cours} en cours</span>
                      <span className="text-slate-500">{cat.total} total</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section Notes Formateur — Compétences acquises/non acquises */}
      {notes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-display font-semibold text-[#0F172A] flex items-center gap-2">
              <Target className="h-5 w-5 text-[#8DC63F]" />
              Bilan des notes formateur
            </h2>
            <SectionInfo text="Ce tableau regroupe les notes saisies par les formateurs par matière/item, normalisées sur /10. Une moyenne >= 7/10 est considérée comme acquise." />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Acquises */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Acquises (Moyenne >= 7/10)
              </h3>
              {(() => {
                const grouped = notes.reduce((acc, n) => {
                  const key = n.categorie || n.matiere || 'Autre';
                  const item = n.item || n.matiere || key;
                  const id = `${key} - ${item}`;
                  if (!acc[id]) acc[id] = { categorie: key, item, notes: [] };
                  acc[id].notes.push(normalizeNote(n));
                  return acc;
                }, {});
                Object.values(grouped).forEach(c => {
                  c.moyenne = c.notes.reduce((a, b) => a + b, 0) / c.notes.length;
                });
                const acquises = Object.values(grouped).filter(c => c.moyenne >= 7).sort((a, b) => b.moyenne - a.moyenne);
                return acquises.length === 0 ? (
                  <p className="text-green-600 text-sm">Aucune compétence acquise pour le moment</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {acquises.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-green-100">
                        <div>
                          <p className="font-medium text-sm text-slate-800">{c.item}</p>
                          {c.categorie !== c.item && <p className="text-xs text-slate-500">{c.categorie}</p>}
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-sm font-bold rounded">
                          {c.moyenne.toFixed(1)}/10
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* A renforcer */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                A renforcer (Moyenne &lt; 7/10)
              </h3>
              {(() => {
                const grouped = notes.reduce((acc, n) => {
                  const key = n.categorie || n.matiere || 'Autre';
                  const item = n.item || n.matiere || key;
                  const id = `${key} - ${item}`;
                  if (!acc[id]) acc[id] = { categorie: key, item, notes: [] };
                  acc[id].notes.push(normalizeNote(n));
                  return acc;
                }, {});
                Object.values(grouped).forEach(c => {
                  c.moyenne = c.notes.reduce((a, b) => a + b, 0) / c.notes.length;
                });
                const nonAcquises = Object.values(grouped).filter(c => c.moyenne < 7).sort((a, b) => a.moyenne - b.moyenne);
                return nonAcquises.length === 0 ? (
                  <p className="text-green-600 text-sm">Toutes les compétences sont acquises !</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {nonAcquises.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-red-100">
                        <div>
                          <p className="font-medium text-sm text-slate-800">{c.item}</p>
                          {c.categorie !== c.item && <p className="text-xs text-slate-500">{c.categorie}</p>}
                        </div>
                        <span className={`px-2 py-1 text-sm font-bold rounded ${
                          c.moyenne < 4 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {c.moyenne.toFixed(1)}/10
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par centre - uniquement si "Tous les centres" */}
        {selectedCentre === 'all' && parCentreData.length > 1 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-display font-semibold text-[#0F172A] mb-1">Répartition des stagiaires par centre</h2>
            <SectionInfo text="Nombre de stagiaires inscrits dans chaque centre de formation" />
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={parCentreData}
                  cx="50%" cy="50%" labelLine={false}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80} dataKey="value"
                >
                  {parCentreData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS_PIE[index % COLORS_PIE.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} stagiaires`, '']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Moyennes par matière/catégorie */}
        {moyennesData.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-display font-semibold text-[#0F172A] mb-1">Moyenne des notes par matière</h2>
            <SectionInfo text="Moyenne des notes formateur regroupées par matière ou catégorie d'évaluation" />
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={moyennesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" style={{ fontSize: 11 }} angle={-30} textAnchor="end" height={70} />
                <YAxis domain={[0, 10]} />
                <Tooltip formatter={(value) => [`${value}`, 'Moyenne']} />
                <Bar dataKey="moyenne" fill="#8DC63F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Présences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Statistiques présences */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-display font-semibold text-[#0F172A] mb-1">Assiduité des stagiaires</h2>
          <SectionInfo text="Répartition des pointages de présence effectués par les formateurs" />
          {presencesChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={presencesChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={120} style={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`${value} pointages`, '']} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {presencesChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {presencesStats?.par_lieu && (
                <div className="flex gap-4 mt-3 justify-center text-xs text-slate-500">
                  <span>Centre : {presencesStats.par_lieu.centre || 0} pointages</span>
                  <span>Entreprise : {presencesStats.par_lieu.entreprise || 0} pointages</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Clock className="h-10 w-10 mb-2" />
              <p className="text-sm">Aucun pointage enregistré</p>
              <p className="text-xs">Les données apparaitront quand les formateurs feront l'appel</p>
            </div>
          )}
        </div>
      </div>

      {/* Classement Top 10 */}
      {classement.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-display font-semibold text-[#0F172A] mb-1">Classement — Top 10 meilleures moyennes</h2>
          <SectionInfo text="Classement des stagiaires avec le plus de notes et les meilleures moyennes formateur" />
          <div className="overflow-x-auto mt-4">
            <table className="w-full" data-testid="top10-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Rang</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Stagiaire</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Centre</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Moyenne</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nb Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {classement.map((stag, idx) => (
                  <tr key={stag.user_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">
                      {idx === 0 ? <span className="text-amber-500 font-bold">#{idx + 1}</span> : `#${idx + 1}`}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{stag.user_info?.prenom} {stag.user_info?.nom}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{stag.centre_formation}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#8DC63F]">{stag.moyenne.toFixed(2)}/10</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{stag.nbEval}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
